/**
 * Bridge between a TipTap document (`JSONContent`) and the **rich-text delta**
 * (`DeltaOp[]`) shared by the CRDT core (`LoroDocument.toDelta`) and the office
 * formats (`office-formats::delta`). This is the pure, unit-testable core of the
 * TipTap↔Loro binding: a paragraph/heading becomes its inline runs' inserts
 * followed by a `"\n"` op whose attributes carry the block style (heading level),
 * exactly how Quill/Loro encode block formatting on the trailing newline.
 *
 * The live editor binding (apply TipTap transactions to Loro and Loro diffs back
 * to TipTap) sits on top of this conversion; keeping the mapping pure means both
 * directions are provable without a DOM.
 */
import type { JSONContent } from "@tiptap/core";
import type { DeltaOp } from "../collab/loro-document";

interface InlineAttrs {
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
}

function marksToAttrs(node: JSONContent): InlineAttrs {
  const attrs: InlineAttrs = {};
  for (const mark of node.marks ?? []) {
    if (mark.type === "bold") {
      attrs.bold = true;
    } else if (mark.type === "italic") {
      attrs.italic = true;
    } else if (mark.type === "underline") {
      attrs.underline = true;
    }
  }
  return attrs;
}

function headingLevel(node: JSONContent): number | undefined {
  if (node.type !== "heading") {
    return undefined;
  }
  // oxlint-disable-next-line typescript/no-unsafe-assignment -- TipTap attrs are untyped JSON; guarded on the next line
  const level = node.attrs?.level;
  return typeof level === "number" ? level : 1;
}

/** Convert a TipTap document to a rich-text delta. */
export function tiptapToDelta(doc: JSONContent): DeltaOp[] {
  const ops: DeltaOp[] = [];
  for (const block of doc.content ?? []) {
    for (const inline of block.content ?? []) {
      if (typeof inline.text !== "string" || inline.text.length === 0) {
        continue;
      }
      const attrs = marksToAttrs(inline);
      ops.push(
        Object.keys(attrs).length > 0
          ? { insert: inline.text, attributes: attrs }
          : { insert: inline.text },
      );
    }
    const heading = headingLevel(block);
    ops.push(heading !== undefined ? { insert: "\n", attributes: { heading } } : { insert: "\n" });
  }
  return ops;
}

/** Rebuild a TipTap document from a rich-text delta. Inline runs accumulate into
 *  the current block; a `"\n"` op closes it, applying its block (heading) style. */
export function deltaToTiptap(ops: DeltaOp[]): JSONContent {
  const blocks: JSONContent[] = [];
  let inline: JSONContent[] = [];

  const flush = (heading?: number): void => {
    const marks = inline.length > 0 ? inline : undefined;
    if (heading !== undefined) {
      blocks.push({ type: "heading", attrs: { level: heading }, content: marks });
    } else {
      blocks.push(marks ? { type: "paragraph", content: marks } : { type: "paragraph" });
    }
    inline = [];
  };

  for (const op of ops) {
    if (op.insert === "\n") {
      flush(op.attributes?.heading);
      continue;
    }
    // A run may contain embedded newlines separating blocks.
    const segments = op.insert.split("\n");
    for (let i = 0; i < segments.length; i++) {
      const seg = segments[i]!;
      if (seg.length > 0) {
        const marks: { type: string }[] = [];
        if (op.attributes?.bold) {
          marks.push({ type: "bold" });
        }
        if (op.attributes?.italic) {
          marks.push({ type: "italic" });
        }
        if (op.attributes?.underline) {
          marks.push({ type: "underline" });
        }
        inline.push(
          marks.length > 0 ? { type: "text", text: seg, marks } : { type: "text", text: seg },
        );
      }
      if (i < segments.length - 1) {
        flush(op.attributes?.heading);
      }
    }
  }
  if (inline.length > 0) {
    flush();
  }
  if (blocks.length === 0) {
    blocks.push({ type: "paragraph" });
  }
  return { type: "doc", content: blocks };
}
